<template>
  <div class="flex flex-col px-0 md:px-4">
    <InStock :loading="stockLoading" class="mt-0 md:order-4 md:mt-2" />

    <PriceDisplay
      v-bind="{
        basePrice,
        finalPrice,
      }"
      class="md:order-5"
    />

    <div v-auto-animate>
      <PaazlTruckDelivery
        v-if="paazlStore.hasTomorrowDelivery"
        :paazl-date="$t('general.delivery_options.tomorrow_delivery.label')"
        :paazl-description="paazlStore.urgentDeliveryDescription"
        class="md:order-6"
      />
    </div>

    <div class="mt-5 md:order-7">
      <AddToCartButton
        :loading="addToCartLoading"
        @on-click="onAddToCartButtonClick"
      />
    </div>

    <div v-if="hasTimer" class="mt-4 md:order-8 md:hidden">
      <ProductTimer />
    </div>

    <UspItems class="mt-5 md:order-9 md:mt-6" />
  </div>

  <div class="mx-0 mt-5 md:mx-4">
    <DeliveryOptions />
  </div>
</template>

<script setup lang="ts">
import InStock from '@/pages/ProductPage/partials/ProductDetailPage/InStock.vue';
import ProductTimer from '@/components/ProductTimer.vue';
import UspItems from '@/pages/ProductPage/partials/ProductDetailPage/UspItems.vue';
import PaazlTruckDelivery from '@/pages/ProductPage/partials/ProductDetailPage/PaazlTruckDelivery.vue';
import DeliveryOptions from '@/pages/ProductPage/partials/ProductDetailPage/DeliveryOptions.vue';
import PriceDisplay from '@/pages/ProductPage/partials/ProductDetailPage/PriceDisplay.vue';
import {
  computed,
  type ComputedRef,
  onBeforeMount,
  onMounted,
  type Ref,
  ref,
} from 'vue';
import { usePaazlStore } from '@/stores/Paazl/PaazlStore.ts';
import { usePaazl } from '@/composables/Paazl.ts';
import AddToCartButton from '@/components/Buttons/AddToCartButton.vue';
import { useSimpleProductAddToCart } from '@/composables/UseSimpleProductAddToCart.ts';

const paazlStore = usePaazlStore();
const paazlComposable = usePaazl();
const addToCart = useSimpleProductAddToCart();

const stockLoading = ref(false);

const addToCartLoading: ComputedRef<boolean> = computed(
  () => addToCart.isRequesting.value || addToCart.isAwaitingRedirect.value
);

const hasTimer: ComputedRef<boolean> = computed(() => !!window.timer);

const basePriceRef: Ref<number | undefined> = ref(
  window.product_prices.base_price
);

const basePrice: ComputedRef<number | undefined> = computed(() =>
  getPrice(basePriceRef)
);

const finalPriceRef: Ref<number | undefined> = ref(
  window.product_prices.final_price
);

const finalPrice: ComputedRef<number | undefined> = computed(() =>
  getPrice(finalPriceRef)
);

function getPrice(priceRef: Ref<number | undefined>) {
  return (priceRef.value ?? 0) / 100;
}

function onAddToCartButtonClick(): void {
  addToCart.addSimpleProductToCart();
}

function initStockLoading(): void {
  // Simulate loading state for stock information :)
  stockLoading.value = true;
  setTimeout(() => {
    stockLoading.value = false;
  }, 1000);
}

onMounted(() => {
  initStockLoading();
});

onBeforeMount(() => {
  paazlStore.requestShippingOptions(paazlComposable.getRequestData(true));
});
</script>
